import React, {useCallback, useMemo, useState} from 'react';
import {Concept} from 'types';
import type {
  ColumnSet,
  ColumnWithLabel,
  ViewMode,
} from 'services/advancedSearch/types';
import {useLocalization} from 'localization';
import {unique} from 'services/utils';
import type {LabelValue} from 'components';
import {Dialog, FieldLabel, Layout, Select, Spacer} from 'components';
import {ColumnPicker} from '../components/ColumnPicker';

const DEFAULT_DISTINCT_FIELD: ColumnWithLabel['name'] = 'productOwnerCode';

export const useDownloadDistinctDialog = (
  allColumnSets: ColumnSet[],
  allColumns: ColumnWithLabel[],
  currentColumns: string[],
  distinctColumns: ColumnWithLabel[],
  onDownload: (
    viewMode: ViewMode,
    columns?: string[],
    distinctOn?: string,
  ) => Promise<void>,
) => {
  const {t} = useLocalization();
  const [open, setOpen] = useState<boolean>(false);

  const [selectedField, setSelectedField] = useState<string>(
    DEFAULT_DISTINCT_FIELD,
  );
  const [selectedColumns, setSelectedColumns] =
    useState<string[]>(currentColumns);

  const [downloading, setDownloading] = useState<boolean>(false);

  const fieldOptions: ReadonlyArray<LabelValue<string>> = useMemo(() => {
    return distinctColumns.map(dc => ({
      value: dc.name,
      label: dc.label,
    }));
  }, [distinctColumns]);

  const handleOpen = useCallback(() => {
    setSelectedField(DEFAULT_DISTINCT_FIELD);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSave = useCallback(() => {
    if (!selectedField) return;

    setDownloading(true);
    onDownload(
      Concept.manifestation,
      unique([selectedField, ...selectedColumns]),
      selectedField,
    ).then(() => {
      setDownloading(false);
      handleClose();
    });
  }, [selectedField, onDownload, selectedColumns, handleClose]);

  const Form = useMemo(() => {
    return (
      <Dialog
        isOpen={open}
        title={t('page.search.downloadDistrinct.download.title')}
        body={
          <Layout flex={1}>
            {/* Disabled view mode selector, as distinct download is only available for manifestation */}
            <FieldLabel
              label={t('page.search.filter.viewmode.label')}
              required
            />
            <Select<ViewMode>
              value={Concept.manifestation}
              options={[
                {
                  value: Concept.manifestation,
                  label: t(`general.entity.${Concept.manifestation}`),
                },
              ]}
              onChange={() => undefined}
              disabled
              required
            />

            <Spacer height={2} />

            <FieldLabel
              label={t('page.search.downloadDistrinct.field.title')}
              required
            />

            <Select<string>
              value={selectedField}
              options={fieldOptions}
              onChange={setSelectedField}
              required
            />
            <Spacer height={2} />

            <ColumnPicker
              viewMode={Concept.manifestation}
              initialColumns={selectedColumns}
              allColumnSets={allColumnSets}
              allColumns={allColumns}
              onChange={setSelectedColumns}
            />
          </Layout>
        }
        dialogItems={[
          {
            itemType: 'action',
            showSpinnerOnClick: true,
            disabled: downloading,
            title: t('general.ok'),
            onClick: () => handleSave(),
          },
          {
            itemType: 'action',
            type: 'reset',
            alignLeft: true,
            disabled: downloading,
            title: t('general.cancel'),
            onClick: () => handleClose(),
          },
        ]}
      />
    );
  }, [
    open,
    t,
    selectedField,
    fieldOptions,
    selectedColumns,
    allColumnSets,
    allColumns,
    downloading,
    handleSave,
    handleClose,
  ]);

  return {
    DownloadDistinctCsvDialog: Form,
    openDownloadDistinctCsvDialog: handleOpen,
  };
};
